import { Circle } from 'lucide-react';
import { LeadActivityResponse } from '../../types/lead';
import { useActivityTypes, resolveActivityType } from '../../hooks/useActivityTypes';

interface Props {
  activities: LeadActivityResponse[];
  isLoading?: boolean;
}

function formatMeta(metadata: Record<string, unknown>): string | null {
  const keys = Object.keys(metadata || {});
  if (!keys.length) return null;
  const parts: string[] = [];
  if (metadata.old && metadata.new) {
    parts.push(`${String(metadata.old)} → ${String(metadata.new)}`);
  } else {
    for (const k of keys) {
      const v = metadata[k];
      if (v === null || v === undefined) continue;
      parts.push(`${k}: ${String(v)}`);
    }
  }
  return parts.join(' · ') || null;
}

export default function LeadActivityTimeline({ activities, isLoading }: Props) {
  const { data: types } = useActivityTypes('lead');

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex gap-3 animate-pulse">
            <div className="h-8 w-8 rounded-full bg-gray-200" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-1/3" />
              <div className="h-3 bg-gray-100 rounded w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!activities.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Circle className="h-10 w-10 mx-auto mb-2 text-gray-300" />
        <p className="text-sm">Nessuna attività registrata</p>
      </div>
    );
  }

  return (
    <ul className="space-y-4">
      {activities.map((a, idx) => {
        const resolved = resolveActivityType(a.type, types);
        const Icon = resolved.icon;
        const meta = formatMeta(a.metadata);
        return (
          <li key={a.id} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div
                className="h-8 w-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${resolved.colore}1A`, color: resolved.colore }}
              >
                <Icon className="h-4 w-4" />
              </div>
              {idx < activities.length - 1 && <div className="flex-1 w-px bg-gray-200 my-1" />}
            </div>
            <div className="flex-1 pb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-gray-900">
                  {resolved.label}
                </span>
                {a.subject && (
                  <span className="text-sm text-gray-700">· {a.subject}</span>
                )}
                <span className="text-xs text-gray-500 ml-auto">
                  {new Date(a.happened_at).toLocaleString('it-IT')}
                </span>
              </div>
              {a.body && (
                <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">{a.body}</p>
              )}
              {meta && (
                <p className="mt-1 text-xs text-gray-500 italic">{meta}</p>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
